/**
 * Tirer vers le bas, tout en haut de la page, pour recharger.
 *
 * Installé en web app (écran d'accueil), le tableau de bord n'a plus la barre
 * du navigateur : ni bouton recharger, ni le geste natif de Chrome ou Safari.
 * On le refait à la main, uniquement dans ce mode-là — dans un onglet, le
 * navigateur le fait déjà et deux gestes superposés se battraient.
 *
 * Rechargement complet plutôt qu'un simple refetch : il récupère aussi une
 * nouvelle version du front déployée entre-temps. L'URL garde la période.
 */

import { useEffect, useRef, useState } from 'react';

// Distance de doigt (px) à dépasser pour déclencher ; l'indicateur suit le
// doigt à moitié vitesse, comme la résistance du geste natif.
const TRIGGER_PX = 140;
const MAX_PULL_PX = 200;

function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * Le doigt est-il posé dans un bloc qui défile verticalement lui-même (chat,
 * barre latérale, liste) ? Le geste y appartient à ce bloc, pas à la page.
 */
function insideScrollBox(target: EventTarget | null): boolean {
  for (let el = target instanceof Element ? target : null; el && el !== document.body; el = el.parentElement) {
    if (el.scrollHeight > el.clientHeight && /auto|scroll/.test(getComputedStyle(el).overflowY)) return true;
  }
  return false;
}

export function PullToRefresh() {
  const [pull, setPull] = useState(0);
  const [reloading, setReloading] = useState(false);
  const startY = useRef<number | null>(null);
  const pullRef = useRef(0);

  useEffect(() => {
    if (!isStandalone()) return;

    const onStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (event.touches.length !== 1 || !touch || window.scrollY > 0 || insideScrollBox(event.target)) {
        startY.current = null;
        return;
      }
      startY.current = touch.clientY;
    };

    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (startY.current === null || !touch) return;
      const distance = touch.clientY - startY.current;
      // Le doigt remonte, ou la page a défilé entre-temps : ce n'est plus le geste.
      if (distance <= 0 || window.scrollY > 0) {
        pullRef.current = 0;
        setPull(0);
        return;
      }
      // Bloque le rebond élastique d'iOS pendant le geste.
      if (event.cancelable) event.preventDefault();
      pullRef.current = Math.min(distance, MAX_PULL_PX);
      setPull(pullRef.current);
    };

    const onEnd = () => {
      if (startY.current !== null && pullRef.current >= TRIGGER_PX) {
        setReloading(true);
        window.location.reload();
      } else {
        setPull(0);
      }
      startY.current = null;
      pullRef.current = 0;
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    // Non passif : `preventDefault` doit pouvoir couper le rebond.
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  if (pull === 0 && !reloading) return null;

  const progress = reloading ? 1 : Math.min(pull / TRIGGER_PX, 1);
  const offset = reloading ? TRIGGER_PX / 2 : pull / 2;

  return (
    <div
      className={`ptr${reloading ? ' ptr--reloading' : ''}${progress >= 1 ? ' ptr--armed' : ''}`}
      style={{ transform: `translate(-50%, ${offset}px)`, opacity: Math.max(progress, 0.3) }}
      role="status"
      aria-label={reloading ? 'Rechargement' : 'Tirer pour recharger'}
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={reloading ? undefined : { transform: `rotate(${progress * 270}deg)` }}
        aria-hidden="true"
      >
        <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
      </svg>
    </div>
  );
}
